import { useState } from "react";
import PropTypes from "prop-types";
import { Shield, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "@shared/components/base/Button";
import { authService } from "@shared/core/services/AuthService";
import { TYPOGRAPHY, THEME } from "@shared/constants/designSystem";
import { cn } from "@shared/utils";

/**
 * MFAChallenge Component
 *
 * Prompts user for MFA verification code during login.
 * Features:
 * - 6-digit code input with auto-focus and validation
 * - Recovery code option
 * - Rate limiting feedback
 * - Error handling with retry mechanism
 */
export function MFAChallenge({ factorId, onSuccess, onCancel }) {
  const [verificationCode, setVerificationCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [attemptCount, setAttemptCount] = useState(0);

  const MAX_ATTEMPTS = 5;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      if (useRecoveryCode) {
        // Verify recovery code - uses different flow than TOTP
        // Recovery codes are single-use and temporarily disable MFA
        await authService.verifyRecoveryCode(recoveryCode, factorId);
      } else {
        // Verify TOTP code via challenge/verify flow
        await authService.challengeMFA(factorId, verificationCode);
      }

      // Success - call onSuccess callback
      if (onSuccess) onSuccess();
    } catch (_err) {
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);

      if (newAttemptCount >= MAX_ATTEMPTS) {
        setError(
          "Too many failed attempts. Please try again later or use a recovery code.",
        );
      } else {
        setError(
          useRecoveryCode
            ? "Invalid recovery code. Please try again."
            : "Invalid verification code. Please check your authenticator app and try again.",
        );
      }

      // Clear input
      if (useRecoveryCode) {
        setRecoveryCode("");
      } else {
        setVerificationCode("");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (value) => {
    // Only allow digits for TOTP code
    const sanitized = value.replace(/\D/g, "");
    setVerificationCode(sanitized);

    // Auto-submit disabled - was causing race conditions where submission
    // happened before all 6 digits were captured in state
    // Users should click "Verify" button instead for reliable submission
  };

  const toggleRecoveryCode = () => {
    setUseRecoveryCode(!useRecoveryCode);
    setVerificationCode("");
    setRecoveryCode("");
    setError("");
  };

  const isRateLimited = attemptCount >= MAX_ATTEMPTS;

  return (
    <div className={`${THEME.COLORS.BG.DEFAULT} rounded-lg p-6 space-y-6`}>
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className={`${THEME.COLORS.BG.ACCENT} rounded-full p-4`}>
            <Shield className="w-8 h-8 text-primary-600" />
          </div>
        </div>
        <div>
          <h3 className={cn(TYPOGRAPHY.HEADING.H3, "text-center mb-2")}>
            Two-Factor Authentication
          </h3>
          <p
            className={cn(TYPOGRAPHY.BODY, "text-gray-600 dark:text-gray-400")}
          >
            {useRecoveryCode
              ? "Enter one of your recovery codes"
              : "Enter the 6-digit code from your authenticator app"}
          </p>
        </div>
      </div>

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {!useRecoveryCode ? (
          <div>
            <label htmlFor="mfa-code" className="sr-only">
              Verification Code
            </label>
            <input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => handleCodeChange(e.target.value)}
              disabled={isLoading || isRateLimited}
              className={`block w-full px-4 py-3 text-center text-2xl font-mono tracking-widest ${THEME.COLORS.BG.INPUT} border ${THEME.COLORS.BORDER.DEFAULT} rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed`}
              placeholder="000000"
              autoFocus
              required
            />
          </div>
        ) : (
          <div className="space-y-3">
            <label htmlFor="recovery-code" className="sr-only">
              Recovery Code
            </label>
            <input
              id="recovery-code"
              type="text"
              value={recoveryCode}
              onChange={(e) =>
                setRecoveryCode(e.target.value.toUpperCase().trim())
              }
              disabled={isLoading || isRateLimited}
              className={`block w-full px-4 py-3 text-center font-mono ${THEME.COLORS.BG.INPUT} border ${THEME.COLORS.BORDER.DEFAULT} rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed`}
              placeholder="XXXXXXXX"
              autoFocus
              required
            />
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p
                className={cn(
                  TYPOGRAPHY.CAPTION,
                  "text-amber-800 dark:text-amber-200",
                )}
              >
                Using a recovery code will temporarily disable MFA on your
                account. You&apos;ll need to re-enable it from Settings after
                logging in.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p
              className={cn(TYPOGRAPHY.BODY, "text-red-800 dark:text-red-200")}
            >
              {error}
            </p>
          </div>
        )}

        {/* Rate Limit Warning */}
        {attemptCount > 0 && attemptCount < MAX_ATTEMPTS && (
          <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
            <AlertCircle className="w-4 h-4" />
            <span>
              {MAX_ATTEMPTS - attemptCount} attempt
              {MAX_ATTEMPTS - attemptCount !== 1 ? "s" : ""} remaining
            </span>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={
            isLoading ||
            isRateLimited ||
            (!useRecoveryCode && verificationCode.length !== 6) ||
            (useRecoveryCode && recoveryCode.length === 0)
          }
          className="w-full"
        >
          {isLoading ? "Verifying..." : "Verify"}
        </Button>
      </form>

      {/* Recovery Code Toggle */}
      {!isRateLimited && (
        <div className="text-center border-t border-gray-200 dark:border-gray-700 pt-4">
          <button
            type="button"
            onClick={toggleRecoveryCode}
            className={cn(
              TYPOGRAPHY.BODY,
              "text-primary-600 dark:text-primary-400 hover:underline focus:outline-none focus:underline",
            )}
          >
            {useRecoveryCode
              ? "Use authenticator code instead"
              : "Use recovery code instead"}
          </button>
        </div>
      )}

      {/* Cancel Button */}
      {onCancel && (
        <div className="text-center">
          <button
            type="button"
            onClick={onCancel}
            className={cn(
              TYPOGRAPHY.BODY,
              "inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus:underline",
            )}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>
        </div>
      )}
    </div>
  );
}

MFAChallenge.propTypes = {
  factorId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
};
